import type { PromptIntentVariant } from './prompt-intent-hint';

export const buildIntentRoutingPreamble = (variant: PromptIntentVariant): string => {
  const focusLine =
    variant === 'general'
      ? 'Pick the correct intent from the user message and conversation history.'
      : `PRIMARY INTENT FOR THIS TURN: "${variant}" — use this unless the message clearly requires a different intent.`;

  return `INTENT ROUTING (this request):
${focusLine}
Return intent + newFilters only. Application code merges with currentParams.`;
};

export const buildIntentSpecificRules = (variant: PromptIntentVariant): string => {
  switch (variant) {
    case 'new_search':
      return `NEW SEARCH RULES:
- Use when currentParams is empty OR user starts fresh ("new search", "start over").
- Extract all filters mentioned in this message into newFilters.
- Bare property-type words ("condos", "houses") are EXCLUSIVE: return all six homeType keys with only the requested type(s) true.
- When currentParams.homeType has all six types true, treat as unset — bare "condos" is new_search/modify_filter with EXCLUSIVE homeType, never add_filter.
- "properties" / "listings" without a specific type → all homeType flags true.`;

    case 'add_filter':
      return `ADD FILTER RULES:
- User is adding to an existing search (e.g. "with a pool", "under 600k", "3 bedrooms").
- Return ONLY new filter(s) from this message in newFilters.
- Use additive language ("add", "include", "also", "with a") for property types — partial homeType is OK.
- Do NOT re-extract filters from history unless the user mentions them again.`;

    case 'modify_filter':
      return `MODIFY FILTER RULES:
- User is changing an existing value ("make it 4 bedrooms", "instead condos", "change budget to 800k").
- Location change when other filters exist → modify_filter (not new_search); only include location in newFilters.
- Message may note preserved filters: "Searching Dallas with your existing filters. Say 'clear filters' to start fresh."
- Property-type "instead" / "switch to" → exclusive homeType object when replacing types.`;

    case 'confirm':
      return `CONFIRM RULES:
- Short affirmations ("yes", "yeah", "sure") after the assistant asked a question.
- Use conversation history to determine what is being confirmed.
- Return newFilters for what the user agreed to (e.g. garage: true, or a clarified city).
- Do NOT extract unrelated filters from older turns.`;

    case 'clarify':
      return `CLARIFY RULES:
- Use needsClarification: true only when nothing meaningful can be extracted.
- If ANY supported filter can be extracted, set needsClarification: false and note unsupported parts in message.
- Non-US/CA locations → clarify with supported-countries message.`;

    case 'general':
    default:
      return `GENERAL INTENT RULES:
- "new_search": fresh search or empty currentParams with a full query.
- "add_filter": adding to existing params ("with", "also", extra criteria).
- "modify_filter": changing a value ("instead", "make it", "change").
- "confirm": "yes"/"ok" answering the last assistant question.
- "clarify": truly ambiguous or only unsupported features.`;
  }
};

export const buildIntentExamples = (variant: PromptIntentVariant): string => {
  switch (variant) {
    case 'new_search':
      return `EXAMPLES (new_search):

User: "I want a house in Toronto under 800k"
{
  "intent": "new_search",
  "newFilters": { "city": "Toronto", "homeType": { "house": true, "condo": false, "townhouse": false, "farm": false, "land": false, "commercial": false }, "priceMax": 800000 },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "I'll search for houses in Toronto under $800k."
}

User: "condos" (currentParams.homeType all true — unset)
{
  "intent": "new_search",
  "newFilters": { "homeType": { "house": false, "condo": true, "townhouse": false, "farm": false, "land": false, "commercial": false } },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "Searching for condos."
}

User: "3 bedroom houses in Austin with big windows"
{
  "intent": "new_search",
  "newFilters": { "city": "Austin", "province": "TX", "country": "US", "homeType": { "house": true, "condo": false, "townhouse": false, "farm": false, "land": false, "commercial": false }, "bedrooms": "3" },
  "confidence": 0.85,
  "needsClarification": false,
  "message": "Searching for 3-bedroom houses in Austin. Note: window filtering isn't available yet."
}`;

    case 'add_filter':
      return `EXAMPLES (add_filter):

User: "with a pool"
{
  "intent": "add_filter",
  "newFilters": { "pool": true },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "Adding pool to your search!"
}

User: "under 600k"
{
  "intent": "add_filter",
  "newFilters": { "priceMax": 600000 },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "Setting budget under $600k."
}

User: "add condos" (homeType already narrowed to houses only)
{
  "intent": "add_filter",
  "newFilters": { "homeType": { "condo": true } },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "Adding condos to your search!"
}`;

    case 'modify_filter':
      return `EXAMPLES (modify_filter):

User: "make it 4 bedrooms"
{
  "intent": "modify_filter",
  "newFilters": { "bedrooms": "4" },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "Changed to 4 bedrooms."
}

User: "change budget to 800k"
{
  "intent": "modify_filter",
  "newFilters": { "priceMax": 800000 },
  "confidence": 0.95,
  "needsClarification": false,
  "message": "Updated budget to $800k."
}

User: "actually show me condos instead"
{
  "intent": "modify_filter",
  "newFilters": { "homeType": { "house": false, "condo": true, "townhouse": false, "farm": false, "land": false, "commercial": false } },
  "confidence": 0.9,
  "needsClarification": false,
  "message": "Switching to condos."
}`;

    case 'confirm':
      return `EXAMPLES (confirm):

User: "yes" (assistant: "Would you like to add a garage?")
{
  "intent": "confirm",
  "newFilters": { "garage": true },
  "confidence": 0.9,
  "needsClarification": false,
  "message": "Adding garage!"
}

User: "yes" (assistant: "Do you mean San Francisco, CA?")
{
  "intent": "confirm",
  "newFilters": { "city": "San Francisco", "province": "CA", "country": "US" },
  "confidence": 0.9,
  "needsClarification": false,
  "message": "Searching in San Francisco, CA."
}`;

    case 'clarify':
      return `EXAMPLES (clarify):

User: "something nice"
{
  "intent": "clarify",
  "newFilters": {},
  "confidence": 0.2,
  "needsClarification": true,
  "clarification": "Could you be more specific? What type of property or location?"
}

User: "properties with big windows and a gym" (nothing extractable)
{
  "intent": "clarify",
  "newFilters": {},
  "confidence": 0.3,
  "needsClarification": true,
  "clarification": "Those filters aren't available yet. Could you specify a location, property type, or price?"
}`;

    case 'general':
    default:
      return `EXAMPLES (mixed intents — pick the best fit):

User: "houses in Austin under 500k" → new_search with city, homeType, priceMax.
User: "with a pool" (has location) → add_filter with pool only.
User: "make it 4 bedrooms" → modify_filter with bedrooms only.
User: "yes" (after assistant question) → confirm from history.`;
  }
};

/** Property-type depth needed for variants that touch homeType. */
export function shouldIncludePropertyTypeRules(variant: PromptIntentVariant): boolean {
  return variant === 'new_search' || variant === 'modify_filter' || variant === 'add_filter' || variant === 'general';
}

export const buildIntentContextRules = (variant: PromptIntentVariant): string => {
  if (variant === 'confirm') {
    return `CONTEXT (confirm):
Use conversation history to interpret "yes", "no", "that one", etc.
- Assistant asked to add garage → newFilters: { garage: true }
- Assistant asked "Do you mean San Francisco, CA?" → newFilters with that city/province/country
Do NOT pull filters from history that the user did not affirm in this message.`;
  }

  return `CONTEXT:
Use history only to interpret short replies. Extract ONLY what the user says in THIS message.
Do NOT re-extract filters from history. Code merges newFilters with currentParams.`;
};
